import React from 'react';
import PropTypes from 'prop-types';
import I18n from 'utils/I18n';

import { LohRegistrationPanel } from '@strava/loh-registration-panel-ui';
import * as ContainerContext from '@strava/container-context';
import {
  logError,
  logMessage,
  setTag,
  setTags,
  setContext,
  withScope
} from 'utils/sentry';
import Microfrontend from '../../../Microfrontend';
import styles from './AccountSignupFormV2.scss';

const {
  AnalyticsContext,
  ErrorLoggingContext,
  TranslationContext
} = ContainerContext;

const { Provider: AnalyticsProvider } = AnalyticsContext;
const { Provider: TranslationProvider } = TranslationContext;
const { Provider: ErrorLoggingProvider } = ErrorLoggingContext;

const analyticsContext = {
  track: (data) => window.Strava.ExternalAnalytics.trackV2(data)
};

const translationContext = {
  t: (key, props = {}) => I18n.t(key, props),
  getLanguage: () => I18n.language()
};

const errorLoggingContext = {
  logError,
  logMessage,
  setContext,
  setTag,
  setTags,
  withScope
};

const AccountSignupFormV2 = ({
  authorizationUrls,
  flashMessage,
  config,
  reCaptchaSiteKey,
  honeyPot,
  isPremium,
  formAuthToken
}) => {
  const { hostname } = window.location;
  const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';

  const urlParams = new URLSearchParams(window.location.search);
  const forceProdMfe = urlParams.get('force_mfe');
  const forcePackage = urlParams.get('force_package');

  const useMfe = (isLocal || forceProdMfe) && !forcePackage;

  const mfeUrl = forceProdMfe
    ? 'https://web-assets.strava.com/assets/federated/loh-registration-panel-wrapper/remoteEntry.js'
    : 'http://localhost:8082/remoteEntry.js';

  const props = {
    authorizationUrls,
    flashMessage,
    config,
    reCaptchaSiteKey,
    honeyPot,
    isPremium,
    formAuthToken
  };

  return (
    <div className={styles.container}>
      {useMfe ? (
        <Microfrontend
          url={mfeUrl}
          scope="strava_loh_registration_panel_wrapper"
          component="./LohRegistrationPanelWrapper"
          appContext={props}
        />
      ) : (
        <AnalyticsProvider value={analyticsContext}>
          <TranslationProvider value={translationContext}>
            <ErrorLoggingProvider value={errorLoggingContext}>
              <LohRegistrationPanel {...props} />
            </ErrorLoggingProvider>
          </TranslationProvider>
        </AnalyticsProvider>
      )}
    </div>
  );
};

AccountSignupFormV2.defaultProps = {
  flashMessage: null
};

AccountSignupFormV2.propTypes = {
  flashMessage: PropTypes.string,
  config: PropTypes.shape({
    appleEnabled: PropTypes.bool.isRequired,
    appleState: PropTypes.string.isRequired // https://developer.apple.com/documentation/sign_in_with_apple/clientconfigi/3230956-state
  }).isRequired,
  authorizationUrls: PropTypes.shape({
    google: PropTypes.string.isRequired,
    facebook: PropTypes.string.isRequired
  }).isRequired,
  honeyPot: PropTypes.string.isRequired,
  isPremium: PropTypes.bool.isRequired,
  reCaptchaSiteKey: PropTypes.string.isRequired,
  formAuthToken: PropTypes.string.isRequired
};

export default AccountSignupFormV2;
